import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, updateDoc, doc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Reservations.css';

const Reservations = ({ tables }) => {
  const [reservations, setReservations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [newReservation, setNewReservation] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    tableId: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    guests: 2,
    specialRequests: '',
    status: 'confirmed'
  });

  useEffect(() => {
    fetchReservations();
  }, [filterDate]);

  const fetchReservations = async () => {
    try {
      const startDate = new Date(filterDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(filterDate);
      endDate.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, 'reservations'),
        where('date', '>=', startDate.toISOString().split('T')[0]),
        where('date', '<=', endDate.toISOString().split('T')[0]),
        orderBy('date'),
        orderBy('time')
      );

      const snapshot = await getDocs(q);
      const reservationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReservations(reservationsData);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  const handleAddReservation = async () => {
    if (!newReservation.customerName || !newReservation.customerPhone || !newReservation.tableId || !newReservation.time) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await addDoc(collection(db, 'reservations'), {
        ...newReservation,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Update table status if needed
      const table = tables.find(t => t.id === newReservation.tableId);
      if (table && table.status === 'available') {
        await updateDoc(doc(db, 'tables', newReservation.tableId), {
          status: 'reserved'
        });
      }

      setShowAddForm(false);
      setNewReservation({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        tableId: '',
        date: new Date().toISOString().split('T')[0],
        time: '',
        guests: 2,
        specialRequests: '',
        status: 'confirmed'
      });
      fetchReservations();
      alert('Reservation created successfully!');
    } catch (error) {
      console.error('Error creating reservation:', error);
      alert('Failed to create reservation');
    }
  };

  const handleUpdateStatus = async (reservationId, newStatus) => {
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        status: newStatus,
        updatedAt: new Date()
      });

      if (newStatus === 'cancelled') {
        const reservation = reservations.find(r => r.id === reservationId);
        if (reservation) {
          await updateDoc(doc(db, 'tables', reservation.tableId), {
            status: 'available'
          });
        }
      }

      fetchReservations();
    } catch (error) {
      console.error('Error updating reservation:', error);
      alert('Failed to update reservation status');
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (window.confirm('Are you sure you want to delete this reservation?')) {
      try {
        const reservation = reservations.find(r => r.id === reservationId);
        await deleteDoc(doc(db, 'reservations', reservationId));
        
        if (reservation) {
          await updateDoc(doc(db, 'tables', reservation.tableId), {
            status: 'available'
          });
        }

        fetchReservations();
        alert('Reservation deleted successfully');
      } catch (error) {
        console.error('Error deleting reservation:', error);
        alert('Failed to delete reservation');
      }
    }
  };

  const getReservationStats = () => {
    const total = reservations.length;
    const confirmed = reservations.filter(r => r.status === 'confirmed').length;
    const completed = reservations.filter(r => r.status === 'completed').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;
    const noShow = reservations.filter(r => r.status === 'no-show').length;
    const totalGuests = reservations.reduce((sum, r) => sum + (r.guests || 0), 0);

    return { total, confirmed, completed, cancelled, noShow, totalGuests };
  };

  const stats = getReservationStats();

  const getTableNumber = (tableId) => {
    const table = tables.find(t => t.id === tableId);
    return table?.tableNumber || 'Unknown';
  };

  const statusColors = {
    confirmed: '#3498db',
    completed: '#2ecc71',
    cancelled: '#e74c3c',
    'no-show': '#95a5a6'
  };

  return (
    <div className="reservations-container">
      <div className="reservations-header">
        <div className="date-selector">
          <label>📅 Select Date:</label>
          <input 
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="date-input"
          />
          <button 
            className="btn-today"
            onClick={() => setFilterDate(new Date().toISOString().split('T')[0])}
          >
            Today
          </button>
        </div>
        <button className="btn-add-reservation" onClick={() => setShowAddForm(true)}>
          + New Reservation
        </button>
      </div>

      <div className="reservation-stats">
        <div className="stat-card-small">
          <p className="stat-label">Total Reservations</p>
          <p className="stat-value">{stats.total}</p>
        </div>
        <div className="stat-card-small">
          <p className="stat-label">Confirmed</p>
          <p className="stat-value" style={{color: '#3498db'}}>{stats.confirmed}</p>
        </div>
        <div className="stat-card-small">
          <p className="stat-label">Completed</p>
          <p className="stat-value" style={{color: '#2ecc71'}}>{stats.completed}</p>
        </div>
        <div className="stat-card-small">
          <p className="stat-label">Total Guests</p>
          <p className="stat-value">{stats.totalGuests}</p>
        </div>
      </div>

      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="reservation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Reservation</h3>
              <button onClick={() => setShowAddForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input 
                    type="text"
                    placeholder="Full name"
                    value={newReservation.customerName}
                    onChange={(e) => setNewReservation({...newReservation, customerName: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input 
                    type="tel"
                    placeholder="10-digit number"
                    value={newReservation.customerPhone}
                    onChange={(e) => setNewReservation({...newReservation, customerPhone: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Email (Optional)</label>
                <input 
                  type="email"
                  placeholder="customer@example.com"
                  value={newReservation.customerEmail}
                  onChange={(e) => setNewReservation({...newReservation, customerEmail: e.target.value})}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Table *</label>
                  <select
                    value={newReservation.tableId}
                    onChange={(e) => setNewReservation({...newReservation, tableId: e.target.value})}
                  >
                    <option value="">Select Table</option>
                    {tables.filter(t => t.status === 'available' || t.status === 'reserved').map(table => (
                      <option key={table.id} value={table.id}>
                        Table {table.tableNumber} ({table.capacity} people - {table.floor})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Number of Guests *</label>
                  <input 
                    type="number"
                    min="1"
                    max="20"
                    value={newReservation.guests}
                    onChange={(e) => setNewReservation({...newReservation, guests: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input 
                    type="date"
                    value={newReservation.date}
                    onChange={(e) => setNewReservation({...newReservation, date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label>Time *</label>
                  <input 
                    type="time"
                    value={newReservation.time}
                    onChange={(e) => setNewReservation({...newReservation, time: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Special Requests (Optional)</label>
                <textarea 
                  placeholder="Any special requirements or notes..."
                  value={newReservation.specialRequests}
                  onChange={(e) => setNewReservation({...newReservation, specialRequests: e.target.value})}
                  rows="3"
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAddReservation}>
                Create Reservation
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="reservations-list">
        {reservations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>No reservations for {filterDate}</h3>
            <p>Create a new reservation to get started</p>
          </div>
        ) : (
          <div className="reservations-timeline">
            {reservations.map(reservation => (
              <div 
                key={reservation.id} 
                className="reservation-card"
                style={{borderLeftColor: statusColors[reservation.status]}}
              >
                <div className="reservation-time">
                  <span className="time-badge">{reservation.time}</span>
                  <span 
                    className="status-badge"
                    style={{backgroundColor: statusColors[reservation.status]}}
                  >
                    {reservation.status}
                  </span>
                </div>
                
                <div className="reservation-details">
                  <h4>{reservation.customerName}</h4>
                  <div className="detail-row">
                    <span>📞 {reservation.customerPhone}</span>
                    {reservation.customerEmail && <span>📧 {reservation.customerEmail}</span>}
                  </div>
                  <div className="detail-row">
                    <span>🪑 Table {getTableNumber(reservation.tableId)}</span>
                    <span>👥 {reservation.guests} guests</span>
                  </div>
                  {reservation.specialRequests && (
                    <div className="special-requests">
                      <strong>Special Requests:</strong> {reservation.specialRequests}
                    </div>
                  )}
                </div>

                <div className="reservation-actions">
                  {reservation.status === 'confirmed' && (
                    <>
                      <button 
                        className="action-btn complete"
                        onClick={() => handleUpdateStatus(reservation.id, 'completed')}
                      >
                        ✓ Complete
                      </button>
                      <button 
                        className="action-btn no-show"
                        onClick={() => handleUpdateStatus(reservation.id, 'no-show')}
                      >
                        ✗ No Show
                      </button>
                      <button 
                        className="action-btn cancel"
                        onClick={() => handleUpdateStatus(reservation.id, 'cancelled')}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDeleteReservation(reservation.id)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reservations;
